/**
 * Affiliate Requests Page
 * Manage product access requests
 * For both Creators (outgoing requests) and Brands (incoming requests)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';
import {
  getCreatorRequests,
  getBrandRequests,
  updateRequestStatus,
  ProductRequest,
} from '../../services/affiliateRequests';
import RequestCard from '../../components/affiliate/RequestCard';

export default function AffiliateRequests() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);

  const [loading, setLoading] = useState(true);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'creator' | 'brand'>('creator');
  const [tabValue, setTabValue] = useState(0);
  const [allRequests, setAllRequests] = useState<ProductRequest[]>([]); // 전체 요청 데이터
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ProductRequest | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [responseAction, setResponseAction] = useState<'approve' | 'reject'>('approve');

  useEffect(() => {
    loadProfile();
  }, [user]);

  useEffect(() => {
    if (profileId) {
      loadRequests();
    }
  }, [profileId, userRole]); // tabValue 제거 - 전체 데이터를 한 번에 가져오므로

  const loadProfile = async () => {
    if (!user) {
      setError('로그인이 필요합니다.');
      setLoading(false);
      return;
    }

    try {

      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (sellerError || !sellerProfile) {
        setError('프로필을 찾을 수 없습니다.');
        setLoading(false);
        return;
      }

      setProfileId(sellerProfile.id);
      // Note: In a real app, you'd determine role based on profile data
      // For now, we'll default to creator and allow viewing both
    } catch (err: any) {
      console.error('Load profile error:', err);
      setError('프로필을 불러올 수 없습니다: ' + err.message);
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    if (!profileId) return;

    try {
      setLoading(true);

      console.log('=== AffiliateRequests loadRequests 시작 ===');
      console.log('userRole:', userRole);
      console.log('profileId:', profileId);

      let data: ProductRequest[] | null = null;
      let error: any = null;

      // 전체 요청 가져오기 (status 필터 없이)
      if (userRole === 'creator') {
        console.log('크리에이터 전체 요청 가져오기...');
        const result = await getCreatorRequests(profileId);
        data = result.data;
        error = result.error;
      } else {
        console.log('브랜드 전체 요청 가져오기...');
        const result = await getBrandRequests(profileId);
        data = result.data;
        error = result.error;
      }

      if (error) throw error;

      console.log('받은 요청 개수:', data?.length || 0);
      setAllRequests(data || []);
    } catch (err: any) {
      console.error('loadRequests 에러:', err);
      setError('요청을 불러올 수 없습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 현재 탭에 표시할 요청들 필터링
  const currentRequests = allRequests.filter((r) => {
    if (tabValue === 0) return r.status === 'pending';
    if (tabValue === 1) return r.status === 'approved';
    if (tabValue === 2) return r.status === 'rejected';
    if (tabValue === 3) return r.status === 'cancelled';
    return false;
  });

  // 각 상태별 카운트
  const pendingCount = allRequests.filter((r) => r.status === 'pending').length;
  const approvedCount = allRequests.filter((r) => r.status === 'approved').length;
  const rejectedCount = allRequests.filter((r) => r.status === 'rejected').length;
  const cancelledCount = allRequests.filter((r) => r.status === 'cancelled').length;

  const handleApproveClick = (request: ProductRequest) => {
    setSelectedRequest(request);
    setResponseAction('approve');
    setResponseDialogOpen(true);
  };

  const handleRejectClick = (request: ProductRequest) => {
    setSelectedRequest(request);
    setResponseAction('reject');
    setResponseDialogOpen(true);
  };

  const handleCancelRequest = async (requestId: string) => {
    try {
      const { error } = await updateRequestStatus(requestId, 'cancelled');
      if (error) throw error;

      setSuccess('요청을 취소했습니다.');
      loadRequests();
    } catch (err: any) {
      setError('요청 취소 실패: ' + err.message);
    }
  };

  const handleSubmitResponse = async () => {
    if (!selectedRequest) return;

    try {
      const { error } = await updateRequestStatus(
        selectedRequest.id,
        responseAction === 'approve' ? 'approved' : 'rejected',
        responseMessage || undefined
      );

      if (error) throw error;

      setSuccess(responseAction === 'approve' ? '요청을 승인했습니다.' : '요청을 거절했습니다.');
      setResponseDialogOpen(false);
      setSelectedRequest(null);
      setResponseMessage('');
      loadRequests();
    } catch (err: any) {
      setError('요청 처리 실패: ' + err.message);
    }
  };

  return (
    <Box sx={styles.pageContainer}>
      <Typography variant="h4" fontWeight="bold" sx={{ ...styles.title, mb: 3 }}>
        어필리에이트 요청 관리
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Role Switcher */}
      <Paper sx={{ ...styles.paper, mb: 3 }}>
        <Tabs
          value={userRole}
          onChange={(e, v) => {
            console.log('===  Role 변경 ===');
            console.log('이전 role:', userRole);
            console.log('새 role:', v);
            setUserRole(v);
            setTabValue(0);
          }}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="크리에이터 뷰 (내 요청)" value="creator" />
          <Tab label="브랜드 뷰 (받은 요청)" value="brand" />
        </Tabs>

        {/* Status Tabs */}
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`대기중 (${pendingCount})`} />
          <Tab label={`승인됨 (${approvedCount})`} />
          <Tab label={`거절됨 (${rejectedCount})`} />
          <Tab label={`취소됨 (${cancelledCount})`} />
        </Tabs>
      </Paper>

      {/* Requests Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : currentRequests.length === 0 ? (
        <Paper sx={{ ...styles.paper, p: 8, textAlign: 'center' }}>
          <Typography variant="h6" sx={styles.text}>
            요청이 없습니다
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={styles.text}>
            {userRole === 'creator'
              ? '마켓플레이스에서 상품 접근을 요청해보세요'
              : '크리에이터가 요청을 보낼 때까지 기다려주세요'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {currentRequests.map((request) => (
            <Grid item xs={12} md={6} key={request.id}>
              <RequestCard
                requestId={request.id}
                productName={request.product?.product_name || '상품명 없음'}
                productImage={request.product?.main_images?.[0] || ''}
                productPrice={request.product?.price || 0}
                commissionRate={request.product?.default_commission_rate || 0}
                commissionType={request.product?.default_commission_type || 'percentage'}
                status={request.status}
                requestedAt={request.requested_at}
                creatorNickName={userRole === 'brand' ? (request as any).creator?.nick_name : undefined}
                creatorName={userRole === 'brand' ? (request as any).creator?.store_name : undefined}
                creatorImage={userRole === 'brand' ? (request as any).creator?.profile_image : undefined}
                creatorSocialLinks={userRole === 'brand' ? (request as any).creator?.social_links : undefined}
                brandNickName={userRole === 'creator' ? request.brand?.nick_name : undefined}
                brandName={userRole === 'creator' ? request.brand?.store_name : undefined}
                brandImage={userRole === 'creator' ? request.brand?.profile_image : undefined}
                creatorMessage={request.creator_message}
                brandResponse={request.brand_response}
                viewType={userRole}
                affiliateStartDate={request.affiliate_settings?.start_date}
                affiliateEndDate={request.affiliate_settings?.end_date}
                affiliateMaxUses={request.affiliate_settings?.max_uses}
                affiliateCurrentUses={request.affiliate_settings?.current_uses}
                onApprove={userRole === 'brand' ? () => handleApproveClick(request) : undefined}
                onReject={userRole === 'brand' ? () => handleRejectClick(request) : undefined}
                onCancel={userRole === 'creator' ? () => handleCancelRequest(request.id) : undefined}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Response Dialog (for brand) */}
      <Dialog
        open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: styles.paper }}
      >
        <DialogTitle sx={styles.text}>
          {responseAction === 'approve' ? '요청 승인' : '요청 거절'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ ...styles.text, mb: 2 }}>
            {selectedRequest?.product?.product_name}에 대한 요청을 {responseAction === 'approve' ? '승인' : '거절'}하시겠습니까?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="응답 메시지 (선택사항)"
            placeholder="크리에이터에게 보낼 메시지를 입력하세요"
            value={responseMessage}
            onChange={(e) => setResponseMessage(e.target.value)}
            sx={styles.input}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            color={responseAction === 'approve' ? 'success' : 'error'}
            onClick={handleSubmitResponse}
          >
            {responseAction === 'approve' ? '승인' : '거절'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
